import { isEqual, isPlainObject } from "lodash-es";

/**
 * Normalizes an audited_changes entry into a `[before, after]` pair.
 *
 * The Audited gem stores update diffs as `[old_value, new_value]` arrays; create and
 * destroy diffs may arrive as bare values. A missing side becomes `null`.
 *
 * @param {*} values - A raw audited_changes entry (array pair or bare value).
 * @returns {Array} A two-element `[before, after]` pair.
 *
 * @example
 * toChangePair(["old", "new"]); // ["old", "new"]
 * toChangePair("created"); // [null, "created"]
 */
export const toChangePair = values =>
  Array.isArray(values) ? [values[0], values[1]] : [null, values];

/**
 * Determines whether a before/after pair can be diffed key-by-key.
 *
 * @param {*} before - The old value from an audited_changes pair.
 * @param {*} after - The new value from an audited_changes pair.
 * @returns {boolean} True when both values are plain JSON objects (not arrays, not null).
 *
 * @example
 * isKeyDiffablePair({ a: 1 }, { a: 2 }); // true
 * isKeyDiffablePair(null, { a: 1 }); // false (create-shaped)
 */
export const isKeyDiffablePair = (before, after) => isPlainObject(before) && isPlainObject(after);

/**
 * Computes a key-level diff between two plain JSON objects.
 *
 * Keys are returned in natural order (before's keys first, then after's new keys).
 * Values are compared deeply, so nested objects compare by content. A key absent on
 * one side yields `undefined` for that side's value.
 *
 * @param {Object} before - The old object (null-safe).
 * @param {Object} after - The new object (null-safe).
 * @returns {Array<{key: string, status: "changed"|"added"|"removed"|"unchanged", before: *, after: *}>} One entry per union key.
 *
 * @example
 * diffObjectKeys({ a: 1, b: "x" }, { a: 2, c: true });
 * // [
 * //   { key: "a", status: "changed", before: 1, after: 2 },
 * //   { key: "b", status: "removed", before: "x", after: undefined },
 * //   { key: "c", status: "added", before: undefined, after: true },
 * // ]
 */
export const diffObjectKeys = (before, after) => {
  const beforeObj = before ?? {};
  const afterObj = after ?? {};
  const keys = [...new Set([...Object.keys(beforeObj), ...Object.keys(afterObj)])];

  return keys.map(key => {
    const inBefore = Object.hasOwn(beforeObj, key);
    const inAfter = Object.hasOwn(afterObj, key);
    const beforeValue = inBefore ? beforeObj[key] : undefined;
    const afterValue = inAfter ? afterObj[key] : undefined;

    let status = "unchanged";
    if (!inBefore && inAfter) status = "added";
    else if (inBefore && !inAfter) status = "removed";
    else if (!isEqual(beforeValue, afterValue)) status = "changed";

    return { key, status, before: beforeValue, after: afterValue };
  });
};

/**
 * Counts keys whose value differs between two plain JSON objects.
 *
 * @param {Object} before - The old object (null-safe).
 * @param {Object} after - The new object (null-safe).
 * @returns {number} The number of changed, added, or removed keys (0 when both values are not plain objects).
 *
 * @example
 * countObjectKeyChanges({ cache_warmer: false }, { cache_warmer: true }); // 1
 */
export const countObjectKeyChanges = (before, after) => {
  if (!isKeyDiffablePair(before, after)) return 0;
  return diffObjectKeys(before, after).filter(entry => entry.status !== "unchanged").length;
};
